// Formatação de data/hora e de documento (CPF/CNPJ) do Radar de Fornecedor.
//
// O horário da consulta vem da API como ISO em UTC (…+00:00 / …Z). Formatamos SEMPRE no
// fuso de Brasília explicitamente — o produto é do setor público brasileiro e o navegador
// do usuário pode estar em outro fuso. Sem `timeZone`, `toLocaleString` usaria o fuso local
// do navegador (foi a causa do bug UTC→BRT: consulta 14h38 exibida como 17h38).

const FUSO_BR = 'America/Sao_Paulo'

/** ISO UTC (ex.: "2026-07-27T17:38:00Z") → "27/07/2026 14:38" (horário de Brasília). */
export function fmtDataHora(iso: string): string {
    const d = new Date(iso)
    // `toLocaleString` sobre uma Invalid Date devolve "Invalid Date" sem lançar — guardamos
    // explicitamente para não exibir isso ao usuário.
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
    try {
        return d.toLocaleString('pt-BR', {
            timeZone: FUSO_BR,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    } catch {
        return iso.slice(0, 10)
    }
}

/**
 * "Hoje" no fuso de Brasília como 'YYYY-MM-DD'.
 *
 * As comparações de vigência (validade de certidão, fim de sanção) são feitas por
 * comparação de STRING contra datas 'YYYY-MM-DD' vindas do backend. Usar
 * `new Date().toISOString()` daria o dia em UTC: entre 21h e 23h59 de Brasília o
 * relógio UTC já virou, então uma certidão que vence HOJE aparecia como vencida
 * (e perdia o checkbox do lote pago). O locale 'en-CA' já formata em YYYY-MM-DD,
 * então a ordenação lexicográfica continua valendo.
 */
export function hojeBR(): string {
    return new Date().toLocaleDateString('en-CA', {timeZone: FUSO_BR})
}

/* ------------------------- Documento (CPF/CNPJ) ------------------------- */
//
// O campo de busca do Radar é ÚNICO para CPF e CNPJ: o comprimento decide o documento
// (11 dígitos = CPF de pessoa física, 14 = CNPJ). Helpers puros, sem React.

/** Máscara de CNPJ (progressiva): "11222333000181" → "11.222.333/0001-81". */
export function formatCnpj(v: string): string {
    const d = (v || '').replace(/\D/g, '').slice(0, 14)
    return d
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2')
}

/** Máscara de CPF (progressiva): "12345678909" → "123.456.789-09". */
export function formatCpf(v: string): string {
    const d = (v || '').replace(/\D/g, '').slice(0, 11)
    let out = d.slice(0, 3)
    if (d.length > 3) out += '.' + d.slice(3, 6)
    if (d.length > 6) out += '.' + d.slice(6, 9)
    if (d.length > 9) out += '-' + d.slice(9)
    return out
}

/**
 * Máscara progressiva do campo único CPF/CNPJ (padrão de mercado): até 11 dígitos
 * formata como CPF (000.000.000-00); do 12º dígito em diante REorganiza para a
 * máscara de CNPJ (00.000.000/0000-00) — quem digita um CNPJ passa pela máscara de
 * CPF no caminho e ela se corrige sozinha.
 */
export function formatCpfCnpj(v: string): string {
    const d = (v || '').replace(/\D/g, '')
    return d.length <= 11 ? formatCpf(d) : formatCnpj(d)
}

/**
 * Valida o dígito verificador do CPF (módulo 11, regra da Receita), ignorando a
 * pontuação. Sequências com todos os dígitos iguais (000…0, 111…1, …) são rejeitadas
 * explicitamente: várias delas fecham na aritmética do módulo 11, mas não são CPFs
 * atribuíveis. Espelha o `cnpjValido` do RadarPanel.
 */
export function cpfValido(v: string): boolean {
    const d = (v || '').replace(/\D/g, '')
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false
    // DV do módulo 11: pesos decrescentes (10..2 no 1º dígito, 11..2 no 2º).
    const dv = (base: string) => {
        let soma = 0
        let peso = base.length + 1
        for (const ch of base) soma += parseInt(ch, 10) * peso--
        const r = (soma * 10) % 11
        return r === 10 ? 0 : r
    }
    const d1 = dv(d.slice(0, 9))
    return d1 === +d[9] && dv(d.slice(0, 9) + d1) === +d[10]
}
